import { Component, Input, OnInit } from '@angular/core';
import { map } from 'rxjs/operators';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TranslateModalConfirm } from 'src/app/modules/translate-app/translate-modal-confirm';
import { CultureService, ICulture, IXenaApp, IXenaAppBundle, IXenaAppPlugin, XenaAppBundleService, XenaAppPluginService, XenaAppService } from 'src/app/xena-api';

@Component({
    selector: 'app-translate-app',
    templateUrl: './translate-app.component.html',
    styleUrls: ['./translate-app.component.scss'],
    standalone: false
})
export class TranslateAppComponent implements OnInit {
  @Input() appId: number;

  _isBundle: boolean;
  get isBundle(): any {
    return this._isBundle;
  }
  @Input() set isBundle(value: any) {
    this._isBundle = value === 'true';
  }


  app: IXenaApp | IXenaAppBundle = { translations: {} };
  plugins: IXenaAppPlugin[] = [];
  appTranslated: UntypedFormGroup;
  pluginTranslated: UntypedFormGroup;
  cultures: ICulture[];
  isOpen: boolean = true;
  isSaving: boolean = false;
  isReadonly: boolean = false;

  _selectedCulture: ICulture = null;
  get selectedCulture(): ICulture {
    return this._selectedCulture;
  }
  set selectedCulture(value: ICulture) {
    this._selectedCulture = value;
    this.selectedCultureChanged(value);
  }

  constructor(
    private fb: UntypedFormBuilder,
    private xenaAppService: XenaAppService,
    private appBundleService: XenaAppBundleService,
    private xenaAppPluginService: XenaAppPluginService,
    private cultureService: CultureService
  ) {
    this.appTranslated = this.fb.group({
      name: '',
      description: '',
      details: '',
      termsMarkdown: '',
    });
    this.pluginTranslated = this.fb.group({
      pluginsArray: this.fb.array([]),
    });
  }

  async ngOnInit() {
    this.cultures = await this.cultureService
      .xenaUICultures({ forceNoPaging: true })
      .pipe(map((c) => c.entities))
      .toPromise();

    if (!this.isBundle) {
      this.app = await this.xenaAppService.get(this.appId).toPromise();
      this.plugins = await this.xenaAppPluginService
        .listByApp(this.appId, { forceNoPaging: true })
        .pipe(map((p) => p.entities))
        .toPromise();

      this.setPluginTranslated();
    } else {
      this.app = await this.appBundleService.get(this.appId).toPromise();
    }

    this.setAppTranslated();
    this.determineReadonly();
  }

  selectedCultureChanged(c: ICulture): void {
    if (!(c.culture in this.app.translations)) {
      this.app.translations[c.culture] = {
        name: '',
        description: '',
        details: '',
        termsMarkdown: '',
      };
    }

    this.setAppTranslated();
    if (!this.isBundle) this.setPluginTranslated();
  }

  setPluginTranslated(): void {
    if (this.selectedCulture && this.plugins) {
      const culture = this.selectedCulture.culture;
      const fa = this.fb.array([]);

      this.plugins.forEach((plugin) => {
        const translationValue = plugin.translations[culture] || { title: '' };
        fa.push(this.fb.control(translationValue.title));
      });

      this.pluginTranslated.setControl('pluginsArray', fa);
    }
  }

  setAppTranslated(): void {
    if (!this.selectedCulture) return;

    this.appTranslated.reset();

    const translation = this.app.translations[this.selectedCulture.culture];
    if (translation) this.appTranslated.setValue(translation);
  }

  determineReadonly(): void {
    this.isReadonly = this.app.isApproved || this.app.isAwaitingApproval;
  }

  hasUnsaved = (): boolean => {
    if (!this.selectedCulture) return false;

    const incoming = this.appTranslated.value;
    const original = this.app.translations[this.selectedCulture.culture];

    if (original.name != incoming.name ||
      original.details != incoming.details ||
      original.description != incoming.description ||
      original.termsMarkdown != incoming.termsMarkdown
    ) {
      return true;
    }

    const incomingTitles: string[] = this.pluginTranslated.get('pluginsArray').value;

    return !this.isBundle && this.plugins.some((plugin: IXenaAppPlugin, index: number) => {
      const incomingTitle = incomingTitles[index];
      const originalPlugin = plugin.translations[this.selectedCulture.culture] || { title: '' };
      return originalPlugin.title != incomingTitle;
    });
  }

  deleteOrUpdateTranslation(isTranslated: boolean, translation: object) {
    if (!isTranslated) {
      delete this.app.translations[this.selectedCulture.culture];
    } else {
      this.app.translations[this.selectedCulture.culture] = translation;
    }
  }


  cancel() {
    if (this.hasUnsaved()) {
      TranslateModalConfirm.open().then(result => result ? this.close() : null);
      return;
    }

    this.close();
  }

  get pluginsArray(): UntypedFormArray {
    return <UntypedFormArray>this.pluginTranslated.get('pluginsArray');
  }

  close() {
    this.isOpen = false;
  }

  async save() {
    this.isSaving = true;
    let appTranslations = this.appTranslated.value;
    let isTranslated = false;
    if (appTranslations.name ||
      appTranslations.description ||
      appTranslations.details ||
      appTranslations.termsMarkdown)
      isTranslated = true;

    try {
      if (!this.isBundle) {
        const appPlugins = <UntypedFormArray>(
          this.pluginTranslated.get('pluginsArray').value
        );
        this.plugins.forEach(async (plugin: IXenaAppPlugin, index: number) => {
          if (appPlugins[index]) {
            isTranslated = true;
            plugin.translations[this.selectedCulture.culture] = {
              title: appPlugins[index],
            };
          } else {
            delete plugin.translations[this.selectedCulture.culture];
          }

          await this.xenaAppPluginService.save(plugin).toPromise();
        });

        this.deleteOrUpdateTranslation(isTranslated, appTranslations);

        await this.xenaAppService.save(this.app).toPromise();
      } else {
        this.deleteOrUpdateTranslation(isTranslated, appTranslations);
        await this.appBundleService.save(this.app).toPromise();
      }

      this.close();
    } catch (e) {
      console.error(e);
    } finally {
      this.isSaving = false;
    }
  }
}
